import React, {
    ChangeEvent,
    Component
} from 'react';
import {connect} from 'react-redux';
import {throttle} from 'lodash';

import {Button} from 'primereact/button';
import {InputText} from 'primereact/inputtext';
import {OverlayPanel} from 'primereact/overlaypanel';

import metaModel from '~/shared/src/utilities/metaModel';
import {__} from '~/shared/src/utilities/messageCatalog';
import {ItemOnlyAttributesForCreation} from '~/shared/src/ci/Item';
import User from '~/shared/src/model/User';
import {
    CheckItemKeyUniquenessParameters,
    CheckItemKeyUniquenessResult
} from '~/shared/src/utilities/modules/data';

import ItemTypeDropdown from '~/frontend/src/components/base/shared/field/ItemTypeDropdown'

import {StoreState} from '~/frontend/src/store';
import xhr from '~/frontend/src/utilities/xhr';
import {XhrComponentState} from '~/frontend/src/reducers/xhr';


const defaultProps = {
    /**
     * The ItemTypeName of the Item to add.
     */
    itemTypeName: '' as string,

    /**
     * Should the add button be disabled.
     */
    disabled: true as boolean
};

type DefaultProps = typeof defaultProps;

type Props = {

    /**
     * The uniqueId of the panel the component is used in.
     *
     * Mostly used to extract the components state from the global one
     * and to generate unique dom element ids.
     */
    uid : string;

    /**
     * The database schema of the Item to add.
     */
    schema : Schema;

    /**
     * Should the MetaModel information shown as defined (raw) <strong>or</strong> should they be processed by MessageCatalog.
     */
    rawMode : boolean;

    /**
     * Should the MetaModel information as defined additionally shown for processed information <strong>or</strong> not.
     * This applies in the form: &lt;processed MetaModel information&gt; (&lt;defined MetaModel information&gt;)
     *
     * Only applicable if <prop>rawMode</prop> is set to false.
     */
    showAdditionalRawNames : boolean;

    /**
     * Callback to invoke if addButton was clicked.
     *
     * @param {ItemAttributesForCreation} item
     */
    onAddItem : ( item : ItemOnlyAttributesForCreation, event : React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLDivElement> ) => void;

    /**
     * The CSS class name(s) to add to the component.
     */
    className? : string;
};

type StateProps = {

    /**
     * The running requests belonging to this component (uid).
     *
     * Will be mapped from global state.
     */
    xhrRequestsForComponent? : XhrComponentState;

    /**
     * The current user.
     *
     * Will be mapped from global state.
     */
    user? : User;
};


export type ComponentProps = Partial<DefaultProps> & Props;
type AllProps = DefaultProps & Props & StateProps;

type State = {
    itemTypeName : string;
    selectedItemTypeName : string;
    lastItemTypeName : string;
    statusId : number;
    key : string;
    keyPlaceholder : string;
    keyDisabled : boolean;
    keyError : string;
    keyCheck : boolean;
    name : string;
    namePlaceholder : string;
    nameError : string;
};

/**
 * AddItemDialog
 */
class AddItemDialog extends Component<AllProps, State> {

    static defaultProps = defaultProps;

    keyInput : HTMLInputElement | null = null
    nameInput : HTMLInputElement | null = null;
    overlayPanel : OverlayPanel | null = null

    /**
     * @param props
     */
    constructor( props : AllProps ) {
        super( props );

        let itemType,
            keyDisabled     = false,
            keyPlaceholder  = '',
            namePlaceholder = '';

        if ( props.itemTypeName ) {
            itemType = metaModel[ props.schema ].getItemType( props.itemTypeName );

            if ( itemType ) {
                // TODO: implement proper solution for YnItemType exception
                keyDisabled = itemType.isKeyAutogenerated || props.itemTypeName === 'YnItemType';
                keyPlaceholder = itemType.keyDefinition;
                namePlaceholder = itemType.nameDefinition;
            }
        }

        this.state = {
            itemTypeName        : props.itemTypeName,
            selectedItemTypeName: props.itemTypeName,
            lastItemTypeName    : props.itemTypeName,
            statusId            : metaModel[ props.schema ].getStatus( 'active' )!.id,
            key                 : '',
            keyPlaceholder,
            keyDisabled,
            keyError            : '',
            keyCheck            : false,
            name                : '',
            namePlaceholder,
            nameError           : ''
        };

        this.onItemTypeDropdownChange = this.onItemTypeDropdownChange.bind( this );
        this.onKeyInputTextChange = this.onKeyInputTextChange.bind( this );
        this.onNameInputTextChange = this.onNameInputTextChange.bind( this );
        this.onCreateButtonClick = this.onCreateButtonClick.bind( this );
        this.onCancelButtonClick = this.onCancelButtonClick.bind( this );
        this.onOverlayPanelHide = this.onOverlayPanelHide.bind( this );

        this.checkKeyUniqueness = throttle( this.checkKeyUniqueness, 200, { trailing: true } );
    }

    /**
     * REDUX stuff
     */
    static getDerivedStateFromProps( props : ComponentProps, state : State ) : Partial<State> {
        const {
                  schema
              } = props;

        let retVal : Partial<State> = {};

        if ( props.itemTypeName !== state.itemTypeName ) {
            retVal.itemTypeName = props.itemTypeName;
            retVal.selectedItemTypeName = props.itemTypeName;
        }

        const itemTypeName = retVal.selectedItemTypeName ?? state.selectedItemTypeName;

        retVal.lastItemTypeName = itemTypeName;

        // ItemType changed
        if ( itemTypeName !== state.lastItemTypeName ) {

            const itemType = metaModel[ schema ].getItemType( itemTypeName );

            let {
                    key,
                    keyError
                }               = state,
                keyDisabled     = false,
                keyPlaceholder  = '',
                namePlaceholder = '';

            if ( itemType ) {
                // TODO: implement proper solution for YnItemType exception
                if ( itemType.isKeyAutogenerated || itemTypeName === 'YnItemType' ) {
                    key = '';
                    keyError = '';
                }
                keyDisabled = itemType.isKeyAutogenerated || itemTypeName === 'YnItemType';
                keyPlaceholder = itemType.keyDefinition;
                namePlaceholder = itemType.nameDefinition;
            }

            retVal = {
                ...retVal,
                key,
                keyPlaceholder,
                keyDisabled,
                keyError,
                namePlaceholder
            };
        }

        return retVal;
    }

    /**
     * REACT lifecycle method
     */
    componentDidMount() {
        this.checkKeyUniqueness();
    }

    /**
     * REACT lifecycle method
     */
    componentDidUpdate( prevProps : ComponentProps, prevState : State ) {
        if ( prevState.selectedItemTypeName !== this.state.selectedItemTypeName || prevState.key !== this.state.key ) {
            this.checkKeyUniqueness();
        }
    }

    /**
     * Checks the given key for uniqueness within the given ItemType against the backend.
     *
     * @private
     */
    checkKeyUniqueness() {
        const {
                  selectedItemTypeName,
                  key
              }          = this.state,
              requestUid = 'checkKeyUniqueness',
              itemTypeId = metaModel[ this.props.schema ].getItemType( selectedItemTypeName )?.id;

        if ( selectedItemTypeName !== '' && key !== '' && itemTypeId ) {

            // abort running check
            if ( this.props.xhrRequestsForComponent?.[ requestUid ] ) {
                xhr.abort( this.props.uid, requestUid );
            }

            const doLoad = async () => {
                const params : CheckItemKeyUniquenessParameters = {
                          schema  : this.props.schema,
                          status  : 'all',
                          itemType: itemTypeId,
                          key
                      },
                      response                                  = await xhr.post(
                          `${BASE_URL}/api/data/checkItemKeyUniqueness`,
                          params,
                          undefined,
                          this.props.uid,
                          requestUid
                      );

                if ( response?.data ) {
                    const responseData = response.data as CheckItemKeyUniquenessResult;

                    if ( responseData.success && !responseData.data.keyIsUnique ) {
                        this.setState( { keyError: __( 'YN.base.finder.item.key.error.notUnique' ) } );
                    }
                }
            };

            doLoad().catch( ( error ) => {
                console.log( error );
            } );
        }
    }

    addItem( event : React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLDivElement> ) : boolean {
        const {
                  schema
              } = this.props,
              {
                  selectedItemTypeName,
                  statusId,
                  key,
                  keyDisabled,
                  keyError,
                  name,
                  nameError
              } = this.state;

        let retVal = false;

        if ( selectedItemTypeName !== '' && ( keyDisabled || ( key && !keyError ) ) && name && !nameError ) {
            retVal = true;

            this.overlayPanel?.hide();

            const item : ItemOnlyAttributesForCreation = {
                itemTypeId: metaModel[ schema ].getItemType( selectedItemTypeName )!.id,
                statusId,
                key       : keyDisabled ? undefined : key,
                name
            };

            this.props.onAddItem( item, event );
        }

        return retVal;
    }

    /**
     * Handler for ItemTypeDropdown change event
     *
     * @private
     */
    onItemTypeDropdownChange( selectedItemTypeName : string ) {
        this.setState( { selectedItemTypeName } );
    }

    /**
     * Handler for key InputText change event
     *
     * @private
     */
    onKeyInputTextChange( event : ChangeEvent<HTMLInputElement> ) {
        let key      = '',
            keyError = '';

        if ( event.target?.value !== undefined ) {
            key = event.target.value;

            if ( !key ) {
                keyError = __( 'YN.base.finder.item.key.error.empty' );
            }
            else if ( / /.test( key ) ) {
                keyError = __( 'YN.base.finder.item.key.error.whitespaces' );
            }

            this.setState( { key, keyError } );
        }
    }

    /**
     * Handler for name InputText change event
     *
     * @private
     */
    onNameInputTextChange( event : ChangeEvent<HTMLInputElement> ) {
        let name      = '',
            nameError = '';

        if ( event.target?.value !== undefined ) {
            name = event.target.value;

            if ( !name ) {
                nameError = __( 'YN.base.finder.item.name.error.empty' );
            }

            this.setState( { name, nameError } );
        }
    }

    /**
     * Handler for create Button click event
     *
     * @private
     */
    onCreateButtonClick( event : React.MouseEvent<HTMLElement> ) {
        this.addItem( event );
    }

    /**
     * Handler for cancel Button click event
     *
     * @private
     */
    onCancelButtonClick() {
        this.overlayPanel?.hide();
    }

    /**
     * Handler for OverlayPanel hide event.
     *
     * @private
     */
    onOverlayPanelHide() {
        this.setState( {
            itemTypeName        : this.props.itemTypeName!,
            selectedItemTypeName: this.props.itemTypeName!,
            lastItemTypeName    : this.props.itemTypeName!,
            key                 : '',
            name                : ''
        } );
    }

    /**
     * REACT lifecycle method
     */
    render() {
        const {
                  uid,
                  schema,
                  rawMode,
                  showAdditionalRawNames,
                  disabled,
                  className
              }                    = this.props,
              {
                  key,
                  keyPlaceholder,
                  keyDisabled,
                  keyError,
                  keyCheck,
                  name,
                  namePlaceholder,
                  nameError
              }                    = this.state,
              itemTypeName         = this.state.selectedItemTypeName !== ''
                  ? this.state.selectedItemTypeName
                  : this.props.itemTypeName,
              createButtonDisabled = !!( itemTypeName === '' || ( key === '' && !keyDisabled ) || keyError || keyCheck || name === '' || nameError );

        return <>
            <Button
                onClick={event => this.overlayPanel?.toggle( event, null )}

                disabled={disabled}

                icon="pi pi-plus-circle"
                tooltip={__( 'YN.base.addItem.button' )}
                tooltipOptions={config.frontend?.buttonTooltipOptions}
                className={`p-button-text p-button-success p-button-rounded ${className}`}
            />
            <OverlayPanel
                ref={el => this.overlayPanel = el}

                appendTo={document.body}
                style={{ width: '25rem' }}
                dismissable={true}
                onHide={this.onOverlayPanelHide}
            >
                <label
                    key={`${uid}_header`}

                    className="yn-menu-title-label"
                >
                    {__( 'YN.base.finder.item.create' )}
                </label>

                <ItemTypeDropdown
                    key={`${uid}_itemTypeDropdown`}

                    uid={uid}

                    schema={schema}
                    rawMode={rawMode}
                    showAdditionalRawNames={showAdditionalRawNames}
                    permission="insert"
                    // TODO: implement proper solution for YnItemType exception
                    genericPermission={itemTypeName === 'YnItemType' ? 'read' : 'nonBlocked'}

                    value={itemTypeName}
                    onChange={this.onItemTypeDropdownChange}
                    onKeyUp={( event : React.KeyboardEvent<HTMLDivElement> ) => {
                        if ( event.key === 'Enter' && !this.addItem( event ) ) {
                            ( this.keyInput as unknown as HTMLInputElement | undefined )?.focus();
                        }
                        else if ( event.key === 'Esc' ) {
                            this.overlayPanel?.hide();
                        }
                    }}

                    label={__( 'YN.base.finder.item.typeLabel' )}
                    style={{ width: '100%' }}
                />

                <div className="field">
                    <label
                        htmlFor={`${uid}_keyInputText`}
                        className="block"
                    >
                        {__( 'YN.base.finder.item.keyLabel' )}
                    </label>
                    <InputText
                        id={`${uid}_keyInputText`}

                        ref={el => this.keyInput = el}

                        value={key}
                        pt={{
                            root: {
                                onChange: this.onKeyInputTextChange,
                                onKeyUp : ( event : React.KeyboardEvent<HTMLDivElement> ) => {
                                    if ( event.key === 'Enter' && !this.addItem( event ) ) {
                                        ( this.nameInput as unknown as HTMLInputElement | undefined )?.focus();
                                    }
                                    else if ( event.key === 'Esc' ) {
                                        this.overlayPanel?.hide();
                                    }
                                }
                            }
                        }}

                        disabled={keyDisabled}
                        className={keyError
                            ? 'p-invalid block'
                            : ''}
                        placeholder={keyPlaceholder}
                        style={{ width: '100%' }}
                        aria-describedby={`${uid}_keyInputText_help`}
                    />
                    {
                        keyCheck
                            ? <small id={`${uid}_keyInputText_help`} className="block">{__( 'YN.base.finder.item.key.check.unique' )}</small>
                            : keyError
                                ? <small id={`${uid}_keyInputText_help`} className="yn-error block">{keyError}</small>
                                : <></>
                    }
                </div>

                <div className="field">
                    <label
                        htmlFor={`${uid}_nameInputText`}
                        className="block"
                    >
                        {__( 'YN.base.finder.item.nameLabel' )}
                    </label>
                    <InputText
                        id={`${uid}_nameInputText`}

                        ref={el => this.nameInput = el}

                        value={name}
                        pt={{
                            root: {
                                onChange: this.onNameInputTextChange,
                                onKeyUp : ( event : React.KeyboardEvent<HTMLDivElement> ) => {
                                    if ( event.key === 'Enter' && !this.addItem( event ) ) {
                                        ( this.keyInput as unknown as HTMLInputElement | undefined )?.focus();
                                    }
                                    else if ( event.key === 'Esc' ) {
                                        this.overlayPanel?.hide();
                                    }
                                }
                            }
                        }}

                        className={nameError
                            ? 'p-invalid block'
                            : ''}
                        placeholder={namePlaceholder}
                        style={{ width: '100%' }}
                        aria-describedby={`${uid}_nameInputText_help`}
                    />
                    {nameError
                        ? <small id={`${uid}_nameInputText_help`} className="yn-error block">{nameError}</small>
                        : <></>}
                </div>

                <hr className="mt-3 mb-3 yn-ruler"/>

                <div className="flex justify-content-end">
                    <Button
                        key={`${uid}_createButton`}

                        label={__( 'YN.base.common.create' )}
                        disabled={createButtonDisabled}
                        onClick={this.onCreateButtonClick}
                        className="p-button-outlined mr-3 yn-button-defaultwidth"
                    />
                    <Button
                        key={`${uid}_cancelButton`}

                        label={__( 'YN.base.common.cancel' )}
                        onClick={this.onCancelButtonClick}
                        className="p-button-secondary p-button-outlined yn-button-defaultwidth"
                    />
                </div>
            </OverlayPanel>
        </>;
    }

}

/**
 * REDUX stuff
 */
function mapStateToProps( state : StoreState, ownProps : ComponentProps ) : StateProps {
    const retVal : StateProps = {
        xhrRequestsForComponent: undefined,
        user                   : undefined
    };

    // get the running xhr request belonging to this component (uid)
    if ( ownProps.uid && state.xhr?.[ ownProps.uid ] ) {
        retVal.xhrRequestsForComponent = state.xhr[ ownProps.uid ];
    }

    // get some session user information
    if ( state.session?.user ) {
        retVal.user = state.session.user;
    }

    return retVal;
}

export default connect<StateProps, undefined, ComponentProps, StoreState>( mapStateToProps, undefined )( AddItemDialog );
